import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ShieldCheck, Package, User, ArrowRight, Copy, CheckCircle, Loader2, Clock } from 'lucide-react';
import TrustTradeLogo from '../components/TrustTradeLogo';
import { resolveEscrowUiState } from '../components/transactionState';
import {
  resolveTransactionPlatformFee,
  resolveTransactionTotal,
  usePlatformConfig,
} from '../context/PlatformConfigContext';

function ShareTransaction() {
  const { config: platformConfig } = usePlatformConfig();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { shareCode } = useParams();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareCode]);

  const fetchData = async () => {
    try {
      // Try to get user (may not be logged in)
      try {
        const userRes = await api.get('/auth/me');
        setUser(userRes.data);
      } catch (e) {
        // Not logged in
        setUser(null);
      }

      // Get transaction preview (public endpoint - use raw axios with /api prefix)
      const txnRes = await api.get(`/share/${shareCode}`);
      setTransaction(txnRes.data);
    } catch (error) {
      console.error('Failed to fetch transaction:', error);
      setError(error.response?.data?.detail || 'Transaction not found');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    // Store the share route to redirect back after login
    const intendedRoute = `/t/${shareCode}`;
    localStorage.setItem('intendedRoute', intendedRoute);
    sessionStorage.setItem('redirectAfterLogin', intendedRoute);
    sessionStorage.setItem('pendingShareCode', shareCode);
    navigate(`/login?redirect=${encodeURIComponent(intendedRoute)}`);
  };

  const handleJoinTransaction = async () => {
    if (!user) {
      handleLogin();
      return;
    }

    setJoining(true);
    try {
      const response = await api.post(`/share/${shareCode}/join`, {});
      
      toast.success('Successfully joined transaction!');
      navigate(`/transactions/${response.data.transaction_id}`);
    } catch (error) {
      console.error('Failed to join transaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to join transaction');
    } finally {
      setJoining(false);
    }
  };

  const getFeePayerLabel = (feePayer) => {
    switch(feePayer) {
      case 'buyer': return 'Buyer pays fee';
      case 'seller': return 'Seller pays fee';
      case 'split': return 'Fee split 50/50';
      default: return 'Fee split 50/50';
    }
  };

  if (loading) {
    return (
      <div className="tt-product min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading transaction...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tt-product min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8 max-w-md text-center">
          <ShieldCheck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Transaction Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={handleLogin}>Sign in to view transaction</Button>
          <Button onClick={() => navigate('/')}>Go to Homepage</Button>
        </Card>
      </div>
    );
  }

  const financialState = resolveEscrowUiState(transaction);
  const platformFeePercent = transaction.platform_fee_percent
    ?? transaction.trusttrade_fee_percent
    ?? platformConfig.platform_fee_percent;
  const platformFee = resolveTransactionPlatformFee(transaction, platformFeePercent);
  // The stored/legacy `total` can include a pre-payment provider-fee guess, so
  // the buyer-facing figure here is item + courier + the buyer's TrustTrade
  // fee only; provider charges are confirmed at secure checkout.
  const hasProviderFreeTotal = transaction.total_before_provider_fees != null;
  const transactionTotal = hasProviderFreeTotal
    ? Number(transaction.total_before_provider_fees)
    : resolveTransactionTotal(transaction, platformFeePercent);

  return (
    <div className="tt-product min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrustTradeLogo dark size="small" showText />
            </div>
            {user ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{user.name}</span>
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={handleLogin}>
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(61,209,148,0.14)', color: '#3DD194' }}>
            <ShieldCheck className="w-4 h-4" />
            Protected Payment · review the agreed transaction details
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">You've Been Invited to a Transaction</h1>
          <p className="text-muted-foreground">Review the details below and join to participate</p>
          <div className="mt-3 inline-flex items-center gap-2 text-muted-foreground text-sm">
            <Clock className="w-4 h-4" />
            Once confirmed by our payment provider: The buyer's payment is protected until the transaction conditions are met. Check the transaction status before handover.
          </div>
        </div>

        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <Badge style={{ backgroundColor: financialState.color, color: 'white' }}>
              {financialState.label}
            </Badge>
            <span className="text-sm text-muted-foreground font-mono">{transaction.share_code}</span>
          </div>

          <div className="space-y-6">
            {/* Item Details */}
            <div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Package className="w-4 h-4" />
                Item Description
              </div>
              <p className="text-foreground font-medium">{transaction.item_description}</p>
              {transaction.item_condition && (
                <Badge className="mt-2 bg-muted text-foreground">{transaction.item_condition}</Badge>
              )}
            </div>

            {/* Parties */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <User className="w-4 h-4" />
                  Buyer
                </div>
                <p className="font-medium text-foreground">{transaction.buyer_name}</p>
              </div>
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <User className="w-4 h-4" />
                  Seller
                </div>
                <p className="font-medium text-foreground">{transaction.seller_name}</p>
              </div>
            </div>

            {/* Price Summary */}
            <div className="bg-primary/5 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Item Value:</span>
                  <span className="font-mono font-medium">R {transaction.item_price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">TrustTrade Platform Fee ({Number(platformFeePercent).toLocaleString()}%):</span>
                  <span className="font-mono font-medium">R {platformFee.toFixed(2)}</span>
                </div>
                <div className="border-t border-border pt-2 mt-2 flex justify-between">
                  <span className="font-semibold">{hasProviderFreeTotal ? 'Total before provider fees:' : 'Buyer Pays Total:'}</span>
                  <span className="font-mono font-bold text-primary text-xl">R {transactionTotal.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Fees and payouts are processed through the payment provider according to the selected fee allocation.{hasProviderFreeTotal ? ' Payment-provider processing and settlement charges are confirmed at secure checkout.' : ''}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Action Button */}
        <div className="text-center">
          {user ? (
            <Button 
              size="lg" 
              onClick={handleJoinTransaction} 
              disabled={joining}
              className="px-8"
              data-testid="join-transaction-btn"
            >
              {joining ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Joining...
                </>
              ) : (
                <>
                  View Transaction
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <Button 
                size="lg" 
                onClick={handleLogin}
                className="px-8"
                data-testid="sign-in-to-join-btn"
              >
                Review transaction
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-sm text-muted-foreground">
                Sign in with the invited email or verified phone to review and join this transaction.
              </p>
            </div>
          )}
        </div>

        {/* Trust Info */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-wrap justify-center items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Provider payment protection
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Agreed delivery and confirmation
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Platform Fee ({Number(platformFeePercent).toLocaleString()}%)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareTransaction;
